import { supabase } from './supabase';
import { logger } from '../utils/logger';
import { Profile, UserGame, Availability, Game } from '../types/profile';

export const profileService = {
  // Get current user's profile
  async getCurrentProfile(): Promise<Profile | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      logger.error('Error fetching profile:', error);
      return null;
    }

    // FIX: Parse JSON strings back to arrays
    if (data) {
      // Map meeple_avatar to avatar for application use
      if (data.meeple_avatar) {
        data.avatar = data.meeple_avatar;
      }

      if (typeof data.play_preferences === 'string') {
        try {
          data.play_preferences = JSON.parse(data.play_preferences);
        } catch (e) {
          logger.error('Failed to parse play_preferences:', e);
          data.play_preferences = null;
        }
      }
      if (typeof data.online_platforms === 'string') {
        try {
          data.online_platforms = JSON.parse(data.online_platforms);
        } catch (e) {
          logger.error('Failed to parse online_platforms:', e);
          data.online_platforms = null;
        }
      }
      if (typeof data.gender === 'string') {
        try {
          data.gender = JSON.parse(data.gender);
        } catch (e) {
          logger.error('Failed to parse gender:', e);
          data.gender = null;
        }
      }
      if (typeof data.gender_preference === 'string') {
        try {
          data.gender_preference = JSON.parse(data.gender_preference);
        } catch (e) {
          logger.error('Failed to parse gender_preference:', e);
          data.gender_preference = null;
        }
      }
    }

    return data;
  },

  // Get profile by ID
  async getProfileById(id: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      logger.error('Error fetching profile:', error);
      return null;
    }

    // FIX: Parse JSON strings back to arrays
    if (data) {
      // Map meeple_avatar to avatar for application use
      if (data.meeple_avatar) {
        data.avatar = data.meeple_avatar;
      }

      if (typeof data.play_preferences === 'string') {
        try {
          data.play_preferences = JSON.parse(data.play_preferences);
        } catch (e) {
          data.play_preferences = null;
        }
      }
      if (typeof data.online_platforms === 'string') {
        try {
          data.online_platforms = JSON.parse(data.online_platforms);
        } catch (e) {
          data.online_platforms = null;
        }
      }
      if (typeof data.gender === 'string') {
        try {
          data.gender = JSON.parse(data.gender);
        } catch (e) {
          data.gender = null;
        }
      }
      if (typeof data.gender_preference === 'string') {
        try {
          data.gender_preference = JSON.parse(data.gender_preference);
        } catch (e) {
          data.gender_preference = null;
        }
      }
    }

    return data;
  },

  // Update profile
  async updateProfile(updates: Partial<Profile>): Promise<{ success: boolean; error?: string; data?: Profile }> {
    logger.log('profileService.updateProfile called with:', updates);

    const { data: { user } } = await supabase.auth.getUser();
    logger.log('Current user:', user?.id);

    if (!user) {
      logger.error('No user found');
      return { success: false, error: 'Not authenticated' };
    }

    // Transform avatar to meeple_avatar for database
    const dbUpdates: any = { ...updates };
    if ('avatar' in dbUpdates) {
      dbUpdates.meeple_avatar = dbUpdates.avatar;
      delete dbUpdates.avatar;
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(dbUpdates)
      .eq('id', user.id)
      .select()
      .single();

    logger.log('Supabase response:', { data, error });

    if (error) {
      logger.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }

    // Parse JSON strings back to arrays and map meeple_avatar to avatar
    if (data) {
      if (data.meeple_avatar) {
        data.avatar = data.meeple_avatar;
      }

      if (typeof data.play_preferences === 'string') {
        try {
          data.play_preferences = JSON.parse(data.play_preferences);
        } catch (e) {
          data.play_preferences = null;
        }
      }
      if (typeof data.online_platforms === 'string') {
        try {
          data.online_platforms = JSON.parse(data.online_platforms);
        } catch (e) {
          data.online_platforms = null;
        }
      }
      if (typeof data.gender === 'string') {
        try {
          data.gender = JSON.parse(data.gender);
        } catch (e) {
          data.gender = null;
        }
      }
      if (typeof data.gender_preference === 'string') {
        try {
          data.gender_preference = JSON.parse(data.gender_preference);
        } catch (e) {
          data.gender_preference = null;
        }
      }
    }

    return { success: true, data };
  },

  // Get user's games
  async getUserGames(userId?: string): Promise<UserGame[]> {
    const { data: { user } } = await supabase.auth.getUser();
    const targetUserId = userId || user?.id;

    if (!targetUserId) return [];

    const { data, error } = await supabase
      .from('user_games')
      .select(`
        *,
        game:game_id (*)
      `)
      .eq('user_id', targetUserId);

    if (error) {
      logger.error('Error fetching user games:', error);
      return [];
    }

    return data || [];
  },

  // Add game to user's collection
  async addUserGame(gameId: string, skillLevel?: string, favorite?: boolean): Promise<{ success: boolean; error?: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('user_games')
      .insert({
        user_id: user.id,
        game_id: gameId,
        skill_level: skillLevel,
        favorite: favorite,
      });

    if (error) {
      logger.error('Error adding user game:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  // Remove game from user's collection
  async removeUserGame(userGameId: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase
      .from('user_games')
      .delete()
      .eq('id', userGameId);

    if (error) {
      logger.error('Error removing user game:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  // Get user's availability
  async getUserAvailability(userId?: string): Promise<Availability[]> {
    const { data: { user } } = await supabase.auth.getUser();
    const targetUserId = userId || user?.id;

    if (!targetUserId) return [];

    const { data, error } = await supabase
      .from('availability')
      .select('*')
      .eq('user_id', targetUserId)
      .order('day_of_week')
      .order('start_time');

    if (error) {
      logger.error('Error fetching availability:', error);
      return [];
    }

    return data || [];
  },

  // Set user availability
  async setAvailability(availability: Omit<Availability, 'id' | 'user_id' | 'created_at'>[]): Promise<{ success: boolean; error?: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    // Delete existing availability
    await supabase
      .from('availability')
      .delete()
      .eq('user_id', user.id);

    // Insert new availability
    const { error } = await supabase
      .from('availability')
      .insert(
        availability.map(slot => ({
          ...slot,
          user_id: user.id,
        }))
      );

    if (error) {
      logger.error('Error setting availability:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  // Search games
  async searchGames(query: string): Promise<Game[]> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .ilike('name', `%${query}%`)
      .limit(20);

    if (error) {
      logger.error('Error searching games:', error);
      return [];
    }

    return data || [];
  },

  // Add game from BGG
  async addGameFromBGG(bggGame: any, skillLevel?: string, favorite?: boolean): Promise<{ success: boolean; error?: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    // First, check if game exists in our database
    const { data: existingGame, error: searchError } = await supabase
      .from('games')
      .select('id')
      .eq('bgg_id', bggGame.id)
      .single();

    let gameId: string;

    if (existingGame) {
      // Game already exists
      gameId = existingGame.id;
    } else {
      // Insert new game
      const { data: newGame, error: insertError } = await supabase
        .from('games')
        .insert({
          name: bggGame.name,
          bgg_id: bggGame.id,
          image_url: bggGame.thumbnail || bggGame.image,
          min_players: bggGame.minPlayers,
          max_players: bggGame.maxPlayers,
        })
        .select()
        .single();

      if (insertError) {
        logger.error('Error inserting game:', insertError);
        return { success: false, error: insertError.message };
      }

      gameId = newGame.id;
    }

    // Now add to user's games
    const { error: userGameError } = await supabase
      .from('user_games')
      .insert({
        user_id: user.id,
        game_id: gameId,
        skill_level: skillLevel,
        favorite: favorite,
      });

    if (userGameError) {
      logger.error('Error adding user game:', userGameError);
      return { success: false, error: userGameError.message };
    }

    return { success: true };
  },

  // Add game manually (from local JSON data)
  async addGameManually(
    gameData: {
      name: string;
      short_name: string;
      year_published: number;
      game_type: string;
      bgg_id: number;
    },
    skillLevel?: string,
    favorite?: boolean
  ): Promise<{ success: boolean; error?: string }> {
    logger.log('addGameManually called with:', gameData);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    logger.log('Current user:', user.id);

    // First, check if game exists in our database (by name for local games)
    const { data: existingGame, error: searchError } = await supabase
      .from('games')
      .select('id')
      .eq('name', gameData.name)
      .maybeSingle();

    logger.log('Existing game check:', { existingGame, searchError });

    let gameId: string;

    if (existingGame) {
      // Game already exists
      gameId = existingGame.id;
      logger.log('Game exists in DB, using ID:', gameId);
    } else {
      // Insert new game
      const { data: newGame, error: insertError } = await supabase
        .from('games')
        .insert({
          name: gameData.name,
          short_name: gameData.short_name,
          bgg_id: gameData.bgg_id,
          year_published: gameData.year_published,
          game_type: gameData.game_type,
          is_system: true, // Mark as a system from our local database
          image_url: null, // No image for local games initially
          min_players: null,
          max_players: null,
        })
        .select()
        .single();

      if (insertError) {
        logger.error('Error inserting game:', insertError);
        return { success: false, error: insertError.message };
      }

      gameId = newGame.id;
      logger.log('Created new game with ID:', gameId);
    }

    // Check if user already has this game
    const { data: existingUserGame, error: checkError } = await supabase
      .from('user_games')
      .select('id')
      .eq('user_id', user.id)
      .eq('game_id', gameId)
      .maybeSingle();

    logger.log('User game check:', { existingUserGame, checkError });

    if (existingUserGame) {
      logger.log('User already has this game');
      return { success: false, error: 'You already have this game in your collection' };
    }

    // Now add to user's games
    logger.log('Adding to user_games...');
    const { error: userGameError } = await supabase
      .from('user_games')
      .insert({
        user_id: user.id,
        game_id: gameId,
        skill_level: skillLevel,
        favorite: favorite || false,
      });

    if (userGameError) {
      logger.error('Error adding user game:', userGameError);
      return { success: false, error: userGameError.message };
    }

    logger.log('Successfully added game to user collection');
    return { success: true };
  },

  // Check if profile is complete
  isProfileComplete(profile: Profile | null, games: UserGame[], availability: Availability[]): boolean {
    if (!profile) return false;

    return !!(
      profile.display_name &&
      profile.bio &&
      profile.location_city &&
      games.length > 0 &&
      availability.length > 0
    );
  },

  // Get nearby profiles
  async getNearbyProfiles(latitude: number, longitude: number, radiusKm: number = 25): Promise<Profile[]> {
    // Simple distance calculation - for production you'd want PostGIS
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .not('location_lat', 'is', null)
      .not('location_lng', 'is', null);

    if (error) {
      logger.error('Error fetching nearby profiles:', error);
      return [];
    }

    // Filter by distance (Haversine formula)
    const filtered = (data || []).filter(profile => {
      if (!profile.location_lat || !profile.location_lng) return false;

      const distance = this.calculateDistance(
        latitude,
        longitude,
        profile.location_lat,
        profile.location_lng
      );

      return distance <= radiusKm;
    });

    return filtered;
  },

  // Calculate distance between two coordinates (Haversine formula)
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  },

  toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  },

    // Create profile if missing (fallback if database trigger failed)
    async createProfileIfMissing(): Promise<{ success: boolean; error?: string }> {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          return { success: false, error: 'Not authenticated' };
        }

        logger.log('Creating missing profile for user:', user.id);

        // Check if profile already exists
        const existing = await this.getCurrentProfile();
        if (existing) {
          logger.log('Profile already exists');
          return { success: true };
        }

        // Extract username from metadata or email
        const username = user.user_metadata?.username || user.email?.split('@')[0] || 'user';

        // Create the profile
        const { error } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            username: username,
            email: user.email,
            display_name: username,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

        if (error) {
          logger.error('Error creating profile:', error);
          return { success: false, error: error.message };
        }

        logger.log('Profile created successfully');
        return { success: true };
      } catch (error) {
        logger.error('Error in createProfileIfMissing:', error);
        return { success: false, error: 'Failed to create profile' };
      }
    },

};